import "./App.css";

import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import HomePage from "./UI/pages/client-side/HomePage";
import CustomProjectPage from "./UI/pages/client-side/CustomProjectPage";
import ProductsCategory from "./UI/pages/client-side/ProductsCategory";
import NoPageError from "./UI/pages/client-side/NoPageError";
import Layout from "./UI/layout/Layout";
import SupportLayout from "./UI/layout/SupportLayout";
import SupportPage from "./UI/pages/client-side/SupportPage";
import Cart from "./UI/pages/client-side/Cart";
import Account from "./UI/pages/client-side/Account";
import LoginModal from "./UI/components/LoginModal.tsx";
import RegisterModal from "./UI/components/RegisterModal.tsx";
import { ProtectedRoute } from "./hooks/ProtectedRoute.jsx";
import { AuthProvider } from "./hooks/useAuth.jsx";
import TermsPage from "./UI/pages/client-side/TermsPage.js";
import ReturPage from "./UI/pages/client-side/ReturPage.js";
import TransportPage from "./UI/pages/client-side/TransportPage.js";
import FilamentListingPage from "./UI/pages/common/FilamentListingPage.tsx";
import ProductDetails from "./UI/pages/client-side/ProductDetails.js";
import { roles } from "./utils/constants.ts";
import DashboardLayout from "./UI/layout/DashboardLayout.tsx";
import DashboardLoginModal from "./UI/components/DashboardLoginModal.tsx";
import DashboardProductsPage from "./UI/pages/admin-side/DashboardProductsPage.tsx";
import DashboardOrdersPage from "./UI/pages/admin-side/DashboardOrdersPage.tsx";
import PrintersManagementLayoutPage from "./UI/pages/admin-side/PrintersManagementLayoutPage.tsx";
import DashboardHome from "./UI/pages/admin-side/DashboardHome.tsx";
import SuppliersPage from "./UI/pages/admin-side/SuppliersPage.tsx";
import PrinterSettings from "./UI/pages/admin-side/PrinterSettings.tsx";
import PrinterFilamentsPage from "./UI/pages/admin-side/PrinterFilamentsPage.tsx";
import TypesPage from "./UI/pages/admin-side/TypesPage.tsx";
import CategoryPage from "./UI/pages/admin-side/CategoryPage.tsx";
import OrderDetails from "./UI/pages/admin-side/OrderDetails.tsx";
import ProductDetailsPage from "./UI/pages/admin-side/ProductDetailsPage.tsx";
import InvoicePage from "./UI/pages/admin-side/InvoicePage.tsx";
import ResetPasswordModal from "./UI/components/ResetPasswordModal.tsx";
import OrderResultPage from "./UI/pages/client-side/OrderResultPage.js";
import PaymentResultPage from "./UI/pages/client-side/PaymentResultPage.js";

const queryCLient = new QueryClient();
function App() {
  return (
    <QueryClientProvider client={queryCLient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="shop/:categoryName" element={<ProductsCategory />} />
              <Route
                path="shop/:categoryName/:productId"
                element={<ProductDetails />}
              />
              <Route path="projects" element={<CustomProjectPage />} />
              <Route
                path="support"
                element={
                  <SupportLayout>
                    <SupportPage />
                  </SupportLayout>
                }
              />
              <Route
                path="transport-payment"
                element={
                  <SupportLayout>
                    <TransportPage />
                  </SupportLayout>
                }
              />
              <Route
                path="terms"
                element={
                  <SupportLayout>
                    <TermsPage />
                  </SupportLayout>
                }
              />
              <Route
                path="retur"
                element={
                  <SupportLayout>
                    <ReturPage />
                  </SupportLayout>
                }
              />
              <Route
                path="account"
                element={
                  <ProtectedRoute role={roles.CLIENT}>
                    <Account />
                  </ProtectedRoute>
                }
              />
              <Route path="cart" element={<Cart />} />
              <Route
                path="placed-order/:status"
                element={<OrderResultPage />}
              />
              <Route path="placed-order" element={<PaymentResultPage />} />
              <Route
                path="colors"
                element={<FilamentListingPage isAdminView={false} />}
              />
              <Route path="reset-password" element={<ResetPasswordModal />} />
              <Route path="login" element={<LoginModal />} />
              <Route path="register" element={<RegisterModal />} />
              <Route path="*" element={<NoPageError />} />
            </Route>

            {/* admin rotes */}
            <Route
              path="/backoffice"
              element={
                <ProtectedRoute role={roles.ADMIN}>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route path="" element={<DashboardHome />} />

              <Route path="category" element={<CategoryPage />} />
              <Route
                path="category/:categoryId"
                element={<DashboardProductsPage />}
              />
              <Route path="invoices" element={<InvoicePage />} />
              <Route path="orders" element={<DashboardOrdersPage />} />
              <Route path="orders/:orderId" element={<OrderDetails />} />
              <Route
                path="category/:categoryId/product/:productId"
                element={<ProductDetailsPage />}
              />

              <Route path="" element={<PrintersManagementLayoutPage />}>
                <Route
                  path="filaments"
                  element={<FilamentListingPage isAdminView={true} />}
                />
                <Route path="suppliers" element={<SuppliersPage />} />
                <Route
                  path="printer/:printerId"
                  element={<PrinterSettings isNewPrinter={false} />}
                />
                <Route
                  path="printer/new"
                  element={<PrinterSettings isNewPrinter={true} />}
                />
                <Route
                  path="filament/:printerId"
                  element={<PrinterFilamentsPage />}
                />
                <Route path="suppliers" element={<SuppliersPage />} />
                <Route path="types" element={<TypesPage />} />
              </Route>

              <Route path="*" element={<NoPageError />} />
            </Route>
            <Route path="/backoffice-login" element={<DashboardLoginModal />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
